// src/pages/Faculty.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Faculty = () => {
  const [faculty, setFaculty] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    department: '',
    qualification: '',
    experience: '',
    bio: '',
    status: 'active',
    category: '',
    priority: 10,
    achievements: '',
    image: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryOrder, setNewCategoryOrder] = useState(10);
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'faculty'), orderBy('priority', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const facultyData = [];
      snapshot.forEach((doc) => {
        facultyData.push({ id: doc.id, ...doc.data() });
      });
      setFaculty(facultyData);
      setLoading(false);
    });

    loadCategories();
    loadDepartments();

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const loadCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'facultyCategories'));
      const catData = [];
      snapshot.forEach((doc) => {
        catData.push({ id: doc.id, ...doc.data() });
      });
      catData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCategories(catData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'departments'));
      const deptData = [];
      snapshot.forEach((doc) => {
        deptData.push({ id: doc.id, name: doc.data().name });
      });
      setDepartments(deptData);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      designation: '',
      department: '',
      qualification: '',
      experience: '',
      bio: '',
      status: 'active',
      category: '',
      priority: 10,
      achievements: '',
      image: ''
    });
    setImageFile(null);
    setImagePreview('');
    setEditingFaculty(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
    // Scroll to top when modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditModal = (item) => {
    setEditingFaculty(item);
    setFormData({
      name: item.name || '',
      email: item.email || '',
      phone: item.phone || '',
      designation: item.designation || '',
      department: item.department || '',
      qualification: item.qualification || '',
      experience: item.experience || '',
      bio: item.bio || '',
      status: item.status || 'active',
      category: item.category || '',
      priority: item.priority || 10,
      achievements: item.achievements || '',
      image: item.image || ''
    });
    if (item.image) setImagePreview(item.image);
    setModalOpen(true);
    // Scroll to top when modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const storageRef = ref(storage, `faculty/${Date.now()}_${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const facultyData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        designation: formData.designation,
        department: formData.department,
        qualification: formData.qualification || '',
        experience: formData.experience ? parseInt(formData.experience) : null,
        bio: formData.bio || '',
        status: formData.status,
        category: formData.category || null,
        priority: parseInt(formData.priority) || 10,
        achievements: formData.achievements || '',
        image: imageUrl,
        updatedAt: new Date()
      };

      if (editingFaculty) {
        if (formData.image && imageFile && formData.image !== imageUrl) {
          try { await deleteObject(ref(storage, formData.image)); } catch (e) {}
        }
        await updateDoc(doc(db, 'faculty', editingFaculty.id), facultyData);
        alert('Faculty updated successfully!');
      } else {
        facultyData.createdAt = new Date();
        await addDoc(collection(db, 'faculty'), facultyData);
        alert('Faculty added successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving faculty:', error);
      alert('Error saving faculty: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Delete this faculty member?')) return;
    
    setLoading(true);
    try {
      if (imageUrl) {
        try { await deleteObject(ref(storage, imageUrl)); } catch (e) {}
      }
      await deleteDoc(doc(db, 'faculty', id));
      alert('Faculty deleted successfully!');
    } catch (error) {
      console.error('Error deleting faculty:', error);
      alert('Error deleting faculty: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    try {
      await addDoc(collection(db, 'facultyCategories'), {
        name: newCategoryName.trim(),
        sortOrder: parseInt(newCategoryOrder) || 10
      });
      setNewCategoryName('');
      setNewCategoryOrder(10);
      setCategoryModalOpen(false);
      await loadCategories();
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Error adding category: ' + error.message);
    }
  };

  const handleDeleteCategory = async (catId, catName) => {
    if (!window.confirm('Remove category? Faculty members using this category will have category field cleared.')) return;
    
    try {
      const updates = faculty.filter(f => f.category === catName).map(f => 
        updateDoc(doc(db, 'faculty', f.id), { category: null })
      );
      await Promise.all(updates);
      await deleteDoc(doc(db, 'facultyCategories', catId));
      await loadCategories();
      alert('Category removed successfully!');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category: ' + error.message);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setNewCategoryName('');
  };

  const getFilteredFaculty = () => {
    let filtered = [...faculty];
    if (filterCategory !== 'all') {
      filtered = filtered.filter(f => f.category === filterCategory);
    }
    filtered.sort((a, b) => {
      const priorityA = a.priority || 99;
      const priorityB = b.priority || 99;
      return sortOrder === 'asc' ? priorityA - priorityB : priorityB - priorityA;
    });
    return filtered;
  };

  const styles = `
    .faculty-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-outline-category {
      background: rgba(139, 92, 246, 0.15);
      border: 2px solid rgba(139, 92, 246, 0.4);
      color: #6d28d9;
    }

    .btn-outline-category:hover {
      background: linear-gradient(135deg, #8b5cf6, #6d28d9);
      color: white;
      border-color: transparent;
      box-shadow: 0 10px 25px rgba(139, 92, 246, 0.4);
    }

    .faculty-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
      gap: 28px;
    }

    .faculty-card {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .faculty-card:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .faculty-image-container {
      position: relative;
      overflow: hidden;
      height: 280px;
      background: linear-gradient(135deg, #0f172a, #334155);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .faculty-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    .faculty-card:hover .faculty-image {
      transform: scale(1.06);
    }

    .priority-badge {
      position: absolute;
      top: 16px;
      right: 16px;
      background: rgba(15, 23, 42, 0.75);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.15);
      color: white;
      padding: 6px 14px;
      border-radius: 6px;
      font-size: 0.75rem;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .faculty-info-box {
      padding: 24px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .faculty-card-name {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 4px;
    }

    .faculty-card-designation {
      color: #0ea5e9;
      font-size: 0.88rem;
      font-weight: 700;
      margin-bottom: 6px;
    }

    .faculty-card-experience {
      font-size: 0.82rem;
      color: #64748b;
      font-weight: 600;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .faculty-card-qualification {
      font-size: 0.82rem;
      background: rgba(16, 185, 129, 0.08);
      border-left: 3px solid #10b981;
      padding: 6px 10px;
      border-radius: 8px;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      color: #0f172a;
      font-weight: 600;
    }

    .faculty-card-department {
      display: inline-block;
      align-self: flex-start;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.75rem;
      font-weight: 700;
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.2);
    }

    .faculty-card-category-badge {
      display: inline-block;
      align-self: flex-start;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.75rem;
      font-weight: 700;
      background: rgba(139, 92, 246, 0.12);
      border: 1px solid rgba(139, 92, 246, 0.2);
      margin-top: 8px;
    }

    .achievements-preview {
      margin-top: 12px;
      padding: 8px;
      background: rgba(245, 158, 11, 0.08);
      border-radius: 8px;
      font-size: 0.7rem;
      color: #f59e0b;
    }

    .faculty-card-actions {
      display: flex;
      gap: 10px;
      padding-top: 16px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
      margin-top: auto;
    }

    .btn-row-action {
      flex: 1;
      padding: 8px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 10000;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 750px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }

    .form-group {
      margin-bottom: 4px;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .image-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .image-preview-wrapper img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      display: block;
    }

    .image-preview-wrapper .remove-image {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .image-preview-wrapper .remove-image:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 4px 0;
    }

    .checkbox-wrapper input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #0ea5e9;
      cursor: pointer;
    }

    .checkbox-wrapper label {
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      cursor: pointer;
      margin: 0;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    /* Category Modal */
    .category-modal-box {
      max-width: 500px;
    }

    .category-list-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      transition: background 0.2s;
    }

    .category-list-item:hover {
      background: rgba(14, 165, 233, 0.05);
    }

    .category-list-item .category-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .category-list-item .category-info .priority-tag {
      font-size: 0.7rem;
      color: #64748b;
      background: rgba(14, 165, 233, 0.1);
      padding: 2px 10px;
      border-radius: 12px;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .faculty-grid {
        grid-template-columns: 1fr;
      }
      .form-grid {
        grid-template-columns: 1fr;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .management-toolbar > div {
        justify-content: center;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && faculty.length === 0) {
    return <LoadingSpinner />;
  }

  const filteredFaculty = getFilteredFaculty();

  return (
    <>
      <style>{styles}</style>
      <div className="faculty-page">
        <div className="welcome-hero">
          <h1>👨‍🏫 Faculty Management</h1>
          <p>Manage faculty profiles, qualifications, achievements, awards, categories, and display priority.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="management-toolbar">
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select 
              className="filter-select" 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            <button className="btn-action-trigger btn-outline-category" onClick={() => setCategoryModalOpen(true)}>
              <i className="fas fa-tags"></i> Manage Categories
            </button>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <select 
              className="filter-select" 
              value={sortOrder} 
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="asc">Priority: Low to High</option>
              <option value="desc">Priority: High to Low</option>
            </select>
            <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
              <i className="fas fa-plus-circle"></i> Add Faculty
            </button>
          </div>
        </div>

        <div className="faculty-grid">
          {filteredFaculty.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-user-slash"></i>
              <h3>No Faculty Found</h3>
              <p>Add new faculty members or change filter.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Add First Faculty
              </button>
            </div>
          ) : (
            filteredFaculty.map((item) => (
              <div className="faculty-card" key={item.id}>
                <div className="faculty-image-container">
                  {item.image ? (
                    <img src={item.image} className="faculty-image" alt={item.name} />
                  ) : (
                    <i className="fas fa-user-graduate" style={{ fontSize: '54px', color: 'white', opacity: 0.6 }}></i>
                  )}
                  <div className="priority-badge">
                    <i className="fas fa-arrow-down-short-wide"></i> Priority: {item.priority || 10}
                  </div>
                </div>
                <div className="faculty-info-box">
                  <h3 className="faculty-card-name">{item.name || 'Unnamed'}</h3>
                  <div className="faculty-card-designation">{item.designation || 'Staff'}</div>
                  <div className="faculty-card-experience">
                    <i className="fas fa-briefcase"></i> {item.experience ? item.experience + ' Years' : 'N/A'}
                  </div>
                  {item.qualification && (
                    <div className="faculty-card-qualification">
                      <i className="fas fa-graduation-cap"></i>
                      <strong>Qualification:</strong> {item.qualification}
                    </div>
                  )}
                  <span className="faculty-card-department">{item.department || 'Unassigned'}</span>
                  {item.category && (
                    <span className="faculty-card-category-badge">
                      <i className="fas fa-tag"></i> {item.category}
                    </span>
                  )}
                  {item.achievements && (
                    <div className="achievements-preview">
                      <i className="fas fa-trophy"></i> {item.achievements.length > 60 ? item.achievements.substring(0, 60) + '...' : item.achievements}
                    </div>
                  )}
                </div>
                <div className="faculty-card-actions">
                  <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                    <i className="fas fa-pen-to-square"></i> Edit
                  </button>
                  <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.image)}>
                    <i className="fas fa-trash-can"></i> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Faculty Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingFaculty ? 'fa-edit' : 'fa-user-plus'}`}></i>
                  {editingFaculty ? 'Edit Faculty' : 'Add New Faculty'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* Full Name */}
                  <div className="form-group full-width">
                    <label>Full Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter full name"
                      required
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@college.edu"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 1234567890"
                    />
                  </div>

                  {/* Designation & Experience */}
                  <div className="form-group">
                    <label>Designation <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      placeholder="e.g., Professor, Associate Professor"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Experience (Years)</label>
                    <input
                      type="number"
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  {/* Qualification */}
                  <div className="form-group full-width">
                    <label>Qualification <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.qualification}
                      onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                      placeholder="e.g., Ph.D. in Computer Science, M.Tech, MBA"
                      required
                    />
                  </div>

                  {/* Department & Category */}
                  <div className="form-group">
                    <label>Department <span className="required">*</span></label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="">-- No Category --</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Priority & Status */}
                  <div className="form-group">
                    <label>Priority (Lower = Higher on Website)</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 10 })}
                      min="1"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Bio */}
                  <div className="form-group full-width">
                    <label>Bio / Description</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      rows="3"
                      placeholder="Brief biography of the faculty member..."
                    />
                  </div>

                  {/* Achievements */}
                  <div className="form-group full-width">
                    <label>Achievements & Awards</label>
                    <textarea
                      value={formData.achievements}
                      onChange={(e) => setFormData({ ...formData, achievements: e.target.value })}
                      rows="2"
                      placeholder="e.g., Best Teacher Award 2023, Research Excellence Award, Published 50+ Papers..."
                    />
                  </div>

                  {/* Profile Photo */}
                  <div className="form-group full-width">
                    <label>Profile Photo</label>
                    <div className="file-upload-wrapper">
                      <i className="fas fa-cloud-upload-alt upload-icon"></i>
                      <div className="upload-text">
                        Drag & drop or <span>browse</span> to upload photo
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                    {imagePreview && (
                      <div className="image-preview-wrapper">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          className="remove-image"
                          onClick={() => {
                            setImagePreview('');
                            setImageFile(null);
                            setFormData({ ...formData, image: '' });
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingFaculty ? 'Update Faculty' : 'Add Faculty'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category Management Modal - Positioned at Top */}
        {categoryModalOpen && (
          <div className="modal-overlay" onClick={closeCategoryModal}>
            <div className="modal-viewport-box category-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className="fas fa-tags" style={{ color: '#8b5cf6' }}></i>
                  Manage Categories
                </h2>
                <button className="modal-close-btn" onClick={closeCategoryModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '12px', color: '#334155' }}>Existing Categories</h4>
                <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid rgba(14, 165, 233, 0.1)', borderRadius: '12px' }}>
                  {categories.length === 0 ? (
                    <p style={{ padding: '20px', color: '#64748b', textAlign: 'center' }}>No categories defined.</p>
                  ) : (
                    categories.map(cat => (
                      <div key={cat.id} className="category-list-item">
                        <div className="category-info">
                          <strong>{cat.name}</strong>
                          <span className="priority-tag">Priority: {cat.sortOrder || 0}</span>
                        </div>
                        <button
                          className="btn-row-action btn-row-delete"
                          onClick={() => handleDeleteCategory(cat.id, cat.name)}
                          style={{ padding: '4px 12px', fontSize: '0.7rem' }}
                        >
                          Delete
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <form onSubmit={handleAddCategory}>
                <div className="form-group">
                  <label>New Category Name</label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="e.g., Professors, Associate Faculty"
                  />
                </div>
                <div className="form-group">
                  <label>Display Priority (sort order)</label>
                  <input
                    type="number"
                    value={newCategoryOrder}
                    onChange={(e) => setNewCategoryOrder(parseInt(e.target.value) || 10)}
                    min="0"
                  />
                </div>
                <div className="modal-footer">
                  <button type="submit" className="btn-submit" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
                    <i className="fas fa-plus"></i> Add Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Faculty;